import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  IconButton,
  TextField,
  Chip,
  alpha,
  useTheme,
  Slide,
} from "@mui/material";
import {
  ArrowBack,
  TableRestaurant,
  People,
  LocationOn,
  Search,
  SwapHoriz,
  LocalOffer,
  ShoppingBag,
} from "@mui/icons-material";
import POSKeyboard from "../POSKeyboard";

const TableInfoSection = ({
  selectedTable,
  onBack,
  searchQuery,
  onSearchChange,
  onChangeTable,
  showOffersOnly,
  onToggleOffers,
  offerCount,
}) => {
  const theme = useTheme();
  const [activeKb, setActiveKb] = useState(null);
  const isTakeaway = !selectedTable;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <IconButton
        onClick={onBack}
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.2),
          },
        }}
      >
        <ArrowBack />
      </IconButton>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flex: 1,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: alpha(
              isTakeaway
                ? theme.palette.warning.main
                : theme.palette.success.main,
              0.1,
            ),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isTakeaway ? (
            <ShoppingBag
              sx={{ fontSize: 22, color: theme.palette.warning.main }}
            />
          ) : (
            <TableRestaurant
              sx={{ fontSize: 22, color: theme.palette.success.main }}
            />
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontSize: "0.95rem" }}
          >
            {isTakeaway
              ? "Takeaway Order"
              : `Table #${selectedTable?.tableNumber || "N/A"}`}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {!isTakeaway && selectedTable?.capacity && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <People sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {selectedTable.capacity} people
                </Typography>
              </Box>
            )}
            {!isTakeaway && selectedTable?.location && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LocationOn sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {selectedTable.location}
                </Typography>
              </Box>
            )}
            {isTakeaway && (
              <Typography variant="caption" color="text.secondary">
                No table assignment required
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Change Table Button - Only show for dine-in */}
      {!isTakeaway && (
        <IconButton
          onClick={onChangeTable}
          sx={{
            bgcolor: alpha(theme.palette.info.main, 0.1),
            height: 44,
            width: 44,
            "&:hover": {
              bgcolor: alpha(theme.palette.info.main, 0.2),
            },
          }}
        >
          <SwapHoriz sx={{ color: theme.palette.info.main }} />
        </IconButton>
      )}

      {/* Offers Toggle Chip */}
      <Chip
        icon={<LocalOffer />}
        label={`${showOffersOnly ? "Showing" : ""} Offers ${offerCount > 0 ? `(${offerCount})` : ""}`}
        onClick={onToggleOffers}
        sx={{
          height: 40,
          px: 1,
          bgcolor: showOffersOnly
            ? theme.palette.error.main
            : alpha(theme.palette.error.main, 0.08),
          color: showOffersOnly ? "#fff" : theme.palette.error.main,
          fontWeight: 700,
          fontSize: "0.85rem",
          border: `2px solid ${
            showOffersOnly
              ? theme.palette.error.main
              : alpha(theme.palette.error.main, 0.3)
          }`,
          cursor: "pointer",
          transition: "all 0.2s ease",
          "& .MuiChip-icon": {
            color: showOffersOnly ? "#fff" : theme.palette.error.main,
            fontSize: 20,
          },
          "&:hover": {
            bgcolor: showOffersOnly
              ? theme.palette.error.dark
              : alpha(theme.palette.error.main, 0.15),
            transform: "translateY(-2px)",
            boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.25)}`,
          },
        }}
      />

      {/* Search Field */}
      <TextField
        type="text"
        inputMode="search"
        autoComplete="off"
        placeholder="Search menu..."
        value={searchQuery}
        onChange={onSearchChange}
        onFocus={() => setActiveKb("search")}
        size="small"
        sx={{
          width: 240,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            height: 40,
          },
        }}
        InputProps={{
          startAdornment: (
            <Search sx={{ mr: 1, fontSize: 20, color: "text.secondary" }} />
          ),
        }}
      />

      {/* POSKeyboard */}
      <Slide
        direction="up"
        in={activeKb === "search"}
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1400,
            display: "flex",
            justifyContent: "center",
            bgcolor: alpha("#000", 0.3),
            pb: 2,
            pt: 1,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 920 }}>
            <POSKeyboard
              value={searchQuery}
              onChange={(val) => onSearchChange({ target: { value: val } })}
              onClose={() => setActiveKb(null)}
              onEnter={() => setActiveKb(null)}
              numericOnly={false}
            />
          </Box>
        </Box>
      </Slide>
    </Paper>
  );
};

export default TableInfoSection;
